"use client";

import React, { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Terminal, Cpu, CircleCheck, MessageSquare, Loader, ShieldCheck, Database } from "lucide-react";
export default function NativeWorkflows() {
  const [progressValue, setProgressValue] = useState(15);
  useEffect(() => {
    const interval = setInterval(() => {
      setProgressValue(prev => {
        if (prev >= 85) return 15;
        return prev + Math.floor(Math.random() * 10) + 5;
      });
    }, 800);
    return () => clearInterval(interval);
  }, []);
  return <section id="native-workflows" className="dark bg-background text-foreground py-24 px-4 sm:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
            Native Workflows
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Execute complex operations directly through iMessage. qOS translates natural language and strict commands into secure, local-first actions.
          </p>
        </div>

        <Tabs defaultValue="input" className="w-full flex flex-col items-center">
          <TabsList className="mb-8 bg-card/50 backdrop-blur-sm border border-border/50 p-1">
            <TabsTrigger value="input" className="px-6 py-2.5 text-sm md:text-base">
              <Terminal className="w-4 h-4 mr-2" />
              1. Input
            </TabsTrigger>
            <TabsTrigger value="logic" className="px-6 py-2.5 text-sm md:text-base">
              <Cpu className="w-4 h-4 mr-2" />
              2. Logic
            </TabsTrigger>
            <TabsTrigger value="execution" className="px-6 py-2.5 text-sm md:text-base">
              <CircleCheck className="w-4 h-4 mr-2" />
              3. Execution
            </TabsTrigger>
          </TabsList>

          <div className="w-full max-w-3xl bg-card/40 backdrop-blur-md border border-border/60 rounded-2xl p-6 sm:p-12 min-h-[400px] flex flex-col justify-center shadow-2xl shadow-black/50">
            
            {}
            <TabsContent value="input" className="w-full m-0 animate-in fade-in zoom-in-95 duration-300">
              <div className="flex flex-col gap-6 items-center">
                <div className="w-full max-w-lg bg-black/80 rounded-xl border border-border/50 overflow-hidden shadow-xl">
                  {}
                  <div className="bg-secondary/20 px-4 py-3 border-b border-border/50 flex items-center justify-between">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                      <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50" />
                    </div>
                    <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                      <MessageSquare className="w-3 h-3" />
                      iMessage Relay
                    </div>
                  </div>
                  {}
                  <div className="p-6 font-mono text-sm sm:text-base space-y-4">
                    <div className="flex text-muted-foreground">
                      <span className="text-primary mr-2"></span>
                      <span>Awaiting command...</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-primary mr-2 mt-0.5"></span>
                      <p className="text-foreground leading-relaxed">
                        <span className="text-accent-foreground font-semibold">@qOS</span> execute workflow <span className="text-chart-2">deploy_agent</span> --target <span className="text-chart-3">mac-mini-01</span> --auth <span className="text-chart-4">strict</span>
                      </p>
                    </div>
                    <div className="flex text-muted-foreground animate-pulse">
                      <span className="text-primary mr-2">_</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  Commands are received via secure iMessage relay, maintaining end-to-end encryption before hitting the local qOS environment.
                </p>
              </div>
            </TabsContent>

            {}
            <TabsContent value="logic" className="w-full m-0 animate-in fade-in zoom-in-95 duration-300">
              <div className="flex flex-col items-center justify-center w-full max-w-md mx-auto space-y-8">
                <div className="relative flex items-center justify-center w-24 h-24 rounded-full bg-secondary/30 border border-border/50 shadow-inner">
                  <Loader className="w-10 h-10 text-primary animate-spin" />
                  <div className="absolute inset-0 rounded-full border border-primary/20 animate-ping" style={{
                  animationDuration: '3s'
                }} />
                </div>
                
                <div className="w-full space-y-5">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm font-mono">
                      <span className="flex items-center text-muted-foreground gap-2">
                        <ShieldCheck className="w-4 h-4 text-chart-2" />
                        Verifying Identity
                      </span>
                      <span className="text-chart-2">OK</span>
                    </div>
                    <div className="flex justify-between items-center text-sm font-mono">
                      <span className="flex items-center text-muted-foreground gap-2">
                        <Database className="w-4 h-4 text-chart-3" />
                        Loading Context Memory
                      </span>
                      <span className="text-chart-3">OK</span>
                    </div>
                    <div className="flex justify-between items-center text-sm font-mono pt-2">
                      <span className="flex items-center text-primary gap-2">
                        <Cpu className="w-4 h-4" />
                        Executing Deployment Logic
                      </span>
                      <span className="text-primary animate-pulse">{progressValue}%</span>
                    </div>
                  </div>
                  
                  <Progress value={progressValue} className="h-1.5" />
                </div>
              </div>
            </TabsContent>

            {}
            <TabsContent value="execution" className="w-full m-0 animate-in fade-in zoom-in-95 duration-300">
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20 shadow-[0_0_30px_rgba(var(--primary),0.2)]">
                  <CircleCheck className="w-10 h-10 text-primary" />
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-2xl font-semibold text-foreground tracking-tight">
                    Workflow Complete
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Agent successfully deployed to dedicated hardware. Confirmation receipt sent back to operator via iMessage.
                  </p>
                </div>

                <div className="w-full max-w-md mt-4 bg-black/60 border border-border/50 rounded-lg p-4 font-mono text-xs sm:text-sm text-left shadow-inner">
                  <div className="flex justify-between text-muted-foreground mb-2 pb-2 border-b border-border/30">
                    <span>Target: mac-mini-01</span>
                    <span className="text-primary">Status: Active</span>
                  </div>
                  <div className="text-muted-foreground space-y-1">
                    <p>{`> Initializing soul matrix... Done.`}</p>
                    <p>{`> Binding tools [fs, net, bash]... Done.`}</p>
                    <p className="text-foreground mt-2">{`> System ready. Awaiting input.`}</p>
                  </div>
                </div>
              </div>
            </TabsContent>

          </div>
        </Tabs>
      </div>
    </section>;
}